import { useEffect, useState } from "react";
import axios from "axios";
import { Download, CheckCircle2, Loader2, AlertCircle, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatPrice(v) {
  if (v == null) return "€ 0,00";
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(v);
}
function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("nl-NL", { day: "2-digit", month: "long", year: "numeric" });
}

export default function InvoicePortal({ token }) {
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/invoices/public/${token}`)
      .then((r) => { if (!cancelled) setInvoice(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || "Factuur niet gevonden of ongeldig token"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/invoices/public/${token}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Factuur-${invoice?.invoice_number || "TTW"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("Downloaden van PDF mislukt. Probeer opnieuw.");
    }
    setDownloading(false);
  };

  const viewPdf = () => window.open(`${API}/invoices/public/${token}/pdf`, "_blank");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#FF3B30]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4" data-testid="portal-error">
        <div className="max-w-md text-center">
          <AlertCircle size={56} className="text-[#E74C3C] mx-auto mb-4" />
          <h1 className="text-white text-2xl font-bold mb-3">Factuur niet gevonden</h1>
          <p className="text-[#888] text-sm mb-6">{error}</p>
          <a href="/" className="inline-flex items-center gap-2 text-[#FF3B30] text-sm hover:underline">
            <ChevronLeft size={16} /> Terug naar TTW Engineering
          </a>
        </div>
      </div>
    );
  }

  const vatRate = invoice.vat_rate ?? 21;

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-10 px-4" data-testid="invoice-portal">
      <div className="max-w-3xl mx-auto">
        <a href="/" className="inline-flex items-center gap-1.5 text-[#888] hover:text-[#FF3B30] text-sm mb-6 transition-colors" data-testid="portal-home">
          <ChevronLeft size={16} /> TTW Engineering
        </a>

        {/* Header card */}
        <div className="bg-gradient-to-br from-[#1a0000] to-[#0f0f0f] border border-[#2A2A2A] rounded-2xl p-8 mb-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="text-[#666] text-xs uppercase tracking-[3px] mb-2">Factuur</p>
              <h1 className="text-white text-3xl font-bold font-mono" data-testid="portal-invoice-number">{invoice.invoice_number}</h1>
              <p className="text-[#888] text-sm mt-2">Datum: {formatDate(invoice.invoice_issued_at || invoice.paid_at)}</p>
              {invoice.payment_status === "paid" && (
                <span className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full text-xs font-bold bg-[#6AB04C]/10 text-[#6AB04C] border border-[#6AB04C]/30">
                  <CheckCircle2 size={13} /> Betaald
                </span>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={viewPdf} variant="outline"
                className="bg-transparent border-[#333] text-white hover:bg-[#1E1E1E] hover:text-white" data-testid="portal-view-pdf">
                Bekijken
              </Button>
              <Button onClick={downloadPdf} disabled={downloading}
                className="bg-[#FF3B30] hover:bg-[#E0342A] text-white font-bold" data-testid="portal-download-pdf">
                {downloading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Download size={16} className="mr-2" />}
                Download PDF
              </Button>
            </div>
          </div>
        </div>

        {/* Klant info */}
        <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-6 mb-6">
          <p className="text-[#666] text-xs uppercase tracking-[2px] mb-3">Factuur aan</p>
          <p className="text-white font-bold" data-testid="portal-customer-name">{invoice.name}</p>
          <p className="text-[#aaa] text-sm">{invoice.address}</p>
          <p className="text-[#aaa] text-sm">{invoice.postcode} {invoice.city}</p>
          <p className="text-[#aaa] text-sm">{invoice.country}</p>
          <p className="text-[#666] text-xs mt-2">{invoice.email}</p>
        </div>

        {/* Line items */}
        <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl overflow-hidden mb-6">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-[#FF3B30]/40">
                <th className="text-left px-5 py-3 text-[#666] text-[11px] font-semibold uppercase tracking-wider">Product</th>
                <th className="text-center px-5 py-3 text-[#666] text-[11px] font-semibold uppercase tracking-wider">Aantal</th>
                <th className="text-right px-5 py-3 text-[#666] text-[11px] font-semibold uppercase tracking-wider">Prijs excl.</th>
                <th className="text-right px-5 py-3 text-[#666] text-[11px] font-semibold uppercase tracking-wider">Totaal excl.</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.items || []).map((it, idx) => {
                const qty = it.quantity || 1;
                const unitIncl = it.price || 0;
                const unitExcl = unitIncl / (1 + vatRate / 100);
                const totalExcl = unitExcl * qty;
                return (
                  <tr key={idx} className="border-b border-[#1E1E1E]/50" data-testid={`portal-line-${idx}`}>
                    <td className="px-5 py-3">
                      <p className="text-white text-sm">{it.title}</p>
                      {it.manufacturer_item && <p className="text-[#555] text-xs font-mono mt-0.5">{it.manufacturer_item}</p>}
                    </td>
                    <td className="px-5 py-3 text-center text-[#ccc] text-sm">{qty}×</td>
                    <td className="px-5 py-3 text-right text-[#ccc] text-sm">{formatPrice(unitExcl)}</td>
                    <td className="px-5 py-3 text-right text-white text-sm">{formatPrice(totalExcl)}</td>
                  </tr>
                );
              })}
              {invoice.shipping_cost > 0 && (
                <tr className="border-b border-[#1E1E1E]/50">
                  <td className="px-5 py-3 text-[#aaa] text-sm">Verzendkosten</td>
                  <td className="px-5 py-3 text-center text-[#ccc] text-sm">1×</td>
                  <td className="px-5 py-3 text-right text-[#ccc] text-sm">{formatPrice(invoice.shipping_cost / (1 + vatRate / 100))}</td>
                  <td className="px-5 py-3 text-right text-white text-sm">{formatPrice(invoice.shipping_cost / (1 + vatRate / 100))}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Totalen */}
        <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-6 mb-8">
          <div className="max-w-sm ml-auto space-y-2">
            <div className="flex justify-between text-sm"><span className="text-[#888]">Subtotaal (excl. btw)</span><span className="text-[#ccc]" data-testid="portal-ex-vat">{formatPrice(invoice.total_ex_vat)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#888]">BTW ({vatRate}%)</span><span className="text-[#F1C40F]" data-testid="portal-vat">{formatPrice(invoice.vat_amount)}</span></div>
            <div className="flex justify-between pt-2 border-t border-[#FF3B30]/40"><span className="text-white font-bold">Totaal (incl. btw)</span><span className="text-[#FF3B30] font-bold text-lg" data-testid="portal-total">{formatPrice(invoice.total_incl_vat || invoice.total)}</span></div>
          </div>
        </div>

        <p className="text-[#555] text-[11px] text-center">
          Deze pagina is toegankelijk via een unieke, versleutelde link uit je bevestigingsmail.
          Bewaar de mail als je later terug wilt naar deze factuur.
        </p>
      </div>
    </div>
  );
}
